import React, { FC, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { ServiceMarketplaceDto, CustomModalActionType } from "@green0meter/api-client";

import {
  useSetServiceImInterested,
  useSetServiceNotRelevant,
  useGetCustomModals,
} from "@api";
import { ConfirmationModal, GetInTouchModal } from "@components/common";
import { Button, Card, CardContent, Stack, IconButton } from "@components/base";
import { useUserStore } from "@store/user";

interface ServiceActionsProps {
  service: ServiceMarketplaceDto;
}

const ServiceActions: FC<ServiceActionsProps> = ({ service }) => {
  const { t, i18n } = useTranslation();
  const { user } = useUserStore();
  const { customModals } = useGetCustomModals();
  const {
    setServiceImInterestedEnabled,
    isPendingSetServiceImInterested,
  } = useSetServiceImInterested(user);
  const {
    setServiceNotRelevant,
  } = useSetServiceNotRelevant(user);
  const [confirmNotRelevant, setConfirmNotRelevant] = useState(false);
  const [isGetInTouchModalOpen, setIsGetInTouchModalOpen] = useState(false);

  const handleBack = (): void => {
    history.back();
  };

  const handleLearnMore = (): void => {
    const currentLocale = i18n.language;

    let translation = service.translations?.find(
      (trans) => trans.languages_code === currentLocale
    );

    if (!translation) {
      const languageCode = currentLocale.split('-')[0];

      translation = service.translations?.find((trans) =>
        trans.languages_code.startsWith(languageCode)
      );
    }

    if (!translation && service.translations?.length > 0) {
      translation = service.translations[0];
    }

    if (translation?.learn_more_link) {
      window.open(translation.learn_more_link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleEnable = (): void => {
    setIsGetInTouchModalOpen(true);
  };

  const handleCloseGetInTouchModal = (): void => {
    setIsGetInTouchModalOpen(false);
  };


  return (
    <>
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between">
            <Stack spacing={1} direction="row" alignItems="center">
              <IconButton size="small" onClick={handleBack}>
                <FeatherIcon icon="arrow-left" size={16} />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FeatherIcon icon="book-open" size={12} />}
                onClick={handleLearnMore}
              >
                {t("serviceActions.learnMore", "Learn more")}
              </Button>
              {setServiceImInterestedEnabled && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FeatherIcon icon="check-circle" size={12} />}
                  onClick={handleEnable}
                  disabled={
                    isPendingSetServiceImInterested ||
                    service.imInterested ||
                    service.isNotRelevant
                  }
                  sx={{
                    backgroundColor: "common.black",
                    color: "common.white",
                    "&:hover": {
                      backgroundColor: "grey.800",
                    },
                  }}
                >
                  {t("serviceActions.enable")}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <GetInTouchModal
        open={isGetInTouchModalOpen}
        data={customModals?.find(
          (modal) => modal.actionType === CustomModalActionType.GetInTouchGeneral,
        )}
        onClose={handleCloseGetInTouchModal}
      />
      <ConfirmationModal
        open={confirmNotRelevant}
        onClose={() => setConfirmNotRelevant(false)}
        onConfirm={() => {
          setConfirmNotRelevant(false);
          setServiceNotRelevant(service.id);
        }}
        title={t("serviceActions.confirmTitle", "Confirm Action")}
        description={t("serviceActions.confirmDescription", "Are you sure you want to proceed with this action?")}
      />
    </>
  );
};

export default ServiceActions;
